import dayjs, { type Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import type {
  DataLockFeature,
  DataLockKnownAction,
} from "../../services/domains/data-lock/types";
import type {
  DataLockAttribute,
  DataLockRecord,
  ScopeSummary,
  ScopeSummaryKind,
} from "./types";

dayjs.extend(customParseFormat);

const FEATURE_LABELS = {
  ORDER: "Khóa đơn hàng",
  ORDER_COST_SHEET: "Khóa bảng kê chi phí",
} satisfies Record<DataLockFeature, string>;

const FIELD_LABELS: Record<string, string> = {
  REGION: "Khu vực",
  DIRECTION: "Chiều hàng",
  OWNERSHIP: "Hình thức sở hữu",
  CUSTOMER: "Khách hàng",
};

const ACTION_LABELS = {
  CREATE: "Tạo",
  UPDATE: "Cập nhật",
  DELETE: "Xóa",
  APPROVE: "Duyệt",
} satisfies Record<DataLockKnownAction, string>;

const OWNERSHIP_LABELS: Record<string, string> = {
  INVESTMENT: "Đầu tư",
  DRY_RENTAL: "Thuê khô",
  SERVICE: "Dịch vụ",
};

const COST_GROUP_LABELS: Record<string, string> = {
  actual: "Chi phí thực tế",
  additionalRevenue: "Doanh thu phát sinh",
};

const getMappedLabel = <Key extends string>(
  labels: Readonly<Record<Key, string>>,
  value: string,
) =>
  Object.prototype.hasOwnProperty.call(labels, value)
    ? labels[value as Key]
    : value;

export const getFeatureLabel = (feature: string) =>
  getMappedLabel(FEATURE_LABELS, feature);

export const getFieldLabel = (field: string) =>
  getMappedLabel(FIELD_LABELS, field);

export const getActionLabel = (action: string) =>
  getMappedLabel(ACTION_LABELS, action);

export const getOwnershipLabel = (ownership: string) =>
  getMappedLabel(OWNERSHIP_LABELS, ownership);

export const getCostGroupLabel = (group: string) =>
  getMappedLabel(COST_GROUP_LABELS, group);

export const getStatusDisplay = (lock: Pick<DataLockRecord, "active">) =>
  lock.active
    ? { label: "Đang bật", color: "green" as const }
    : { label: "Đã tắt", color: "default" as const };

const CALENDAR_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})(?:$|[T ])/;

const parseCalendarDate = (value?: string | null): Dayjs | null => {
  if (!value) {
    return null;
  }

  const calendarDate = value.match(CALENDAR_DATE_PREFIX)?.[1];
  if (!calendarDate) {
    return null;
  }

  const date = dayjs(calendarDate, "YYYY-MM-DD", true);
  return date.isValid() ? date : null;
};

const parseDateTime = (value?: string | null): Dayjs | null => {
  if (!value) {
    return null;
  }

  const calendarDate = value.match(CALENDAR_DATE_PREFIX)?.[1];
  if (calendarDate && !dayjs(calendarDate, "YYYY-MM-DD", true).isValid()) {
    return null;
  }

  const date = dayjs(value);
  return date.isValid() ? date : null;
};

export const formatDataLockDate = (value?: string | null) => {
  const date = parseCalendarDate(value);
  if (!date) {
    return "-";
  }

  return date.format("DD/MM/YYYY");
};

export const formatDataLockDateTime = (value?: string | null) => {
  const date = parseDateTime(value);
  if (!date) {
    return "-";
  }

  return date.format("DD/MM/YYYY HH:mm:ss");
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const getActorLabel = (actor: unknown) => {
  if (!isRecord(actor) || Array.isArray(actor)) {
    return "-";
  }

  if (typeof actor.accountName === "string" && actor.accountName.length > 0) {
    return actor.accountName;
  }

  return typeof actor.name === "string" && actor.name.length > 0
    ? actor.name
    : "-";
};

const getPopulatedDisplay = (value: unknown) => {
  if (!isRecord(value)) {
    return undefined;
  }

  for (const key of ["label", "name", "code"] as const) {
    const display = value[key];
    if (typeof display === "string" && display) {
      return display;
    }
  }

  return undefined;
};

const findPopulatedDisplay = (
  populatedValues: unknown[],
  matchKey: "value" | "_id",
  value: string,
) => {
  for (const populatedValue of populatedValues) {
    if (!isRecord(populatedValue) || populatedValue[matchKey] !== value) {
      continue;
    }

    const display = getPopulatedDisplay(populatedValue);
    if (display) {
      return display;
    }
  }

  return undefined;
};

export const getAttributeDisplayValues = (
  attribute: DataLockAttribute,
): string[] => {
  const values = Array.isArray(attribute?.values)
    ? attribute.values.filter((value) => typeof value === "string")
    : [];
  const populatedValues: unknown[] = Array.isArray(attribute?.populatedValues)
    ? attribute.populatedValues
    : [];

  return values.map((value) => {
    const populatedLabel =
      findPopulatedDisplay(populatedValues, "value", value) ||
      findPopulatedDisplay(populatedValues, "_id", value);

    if (populatedLabel) {
      return populatedLabel;
    }

    if (attribute.field === "OWNERSHIP") {
      return getOwnershipLabel(value);
    }

    return value;
  });
};

const getNonEmptyStringValues = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter(
        (item): item is string => typeof item === "string" && item.length > 0,
      )
    : [];

const summarizeOrderAttributes = (attributes: unknown): ScopeSummary[] => {
  if (!Array.isArray(attributes)) {
    return [];
  }

  const valuesByField = new Map<string, Set<string>>();

  for (const attribute of attributes) {
    if (
      !isRecord(attribute) ||
      typeof attribute.field !== "string" ||
      !attribute.field
    ) {
      continue;
    }

    if (!valuesByField.has(attribute.field)) {
      valuesByField.set(attribute.field, new Set());
    }

    const fieldValues = valuesByField.get(attribute.field);
    for (const value of getNonEmptyStringValues(attribute.values)) {
      fieldValues?.add(value);
    }
  }

  return Array.from(valuesByField.entries())
    .map(([field, values]) => ({
      label: getFieldLabel(field),
      count: values.size,
    }))
    .filter((summary) => summary.count > 0);
};

const getCostLineIdentity = (line: unknown) => {
  if (!isRecord(line)) {
    return undefined;
  }

  if (typeof line.configId === "string" && line.configId) {
    return line.configId;
  }

  return typeof line.code === "string" && line.code ? line.code : undefined;
};

export const getScopeSummary = (
  lock: DataLockRecord,
  kind: ScopeSummaryKind,
): ScopeSummary[] => {
  if (lock?.feature === "ORDER") {
    if (!isRecord(lock.orderScope)) {
      return [];
    }

    return summarizeOrderAttributes(
      lock.orderScope[
        kind === "restricted"
          ? "restrictedAttributes"
          : "whitelistedAttributes"
      ],
    );
  }

  if (
    lock?.feature !== "ORDER_COST_SHEET" ||
    kind === "whitelisted" ||
    !isRecord(lock.costSheetScope)
  ) {
    return [];
  }

  const restrictedGroups = new Set(
    getNonEmptyStringValues(lock.costSheetScope.restrictedGroups),
  );
  const restrictedLines = new Set<string>();
  if (Array.isArray(lock.costSheetScope.restrictedLines)) {
    for (const line of lock.costSheetScope.restrictedLines) {
      const identity = getCostLineIdentity(line);
      if (identity) {
        restrictedLines.add(identity);
      }
    }
  }

  return [
    {
      label: "Nhóm chi phí",
      count: restrictedGroups.size,
    },
    {
      label: "Dòng chi phí",
      count: restrictedLines.size,
    },
  ].filter((summary) => summary.count > 0);
};
